import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Handshake, Upload, Trash2, Edit2, X, Globe, GripVertical, Image as ImageIcon } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Switch,
} from "@/components/ui/switch";

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const [formData, setFormData] = useState({ name: "", website: "", file: null });

  useEffect(() => { fetchPartners(); }, []);

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API}/partners?active_only=false`, { withCredentials: true });
      setPartners(res.data);
    } catch { toast.error("Failed to load partners"); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error("Partner name is required"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("name", formData.name);
    fd.append("website", formData.website);
    if (formData.file) fd.append("file", formData.file);
    try {
      await axios.post(`${API}/partners`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Partner added!");
      setDialogOpen(false);
      setFormData({ name: "", website: "", file: null });
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchPartners();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add partner"); }
    finally { setUploading(false); }
  };

  const handleUpdate = async () => {
    if (!selected) return;
    try {
      await axios.put(`${API}/partners/${selected.partner_id}`, {
        name: selected.name, website: selected.website, is_active: selected.is_active
      }, { withCredentials: true });
      toast.success("Partner updated!");
      setEditDialogOpen(false);
      fetchPartners();
    } catch { toast.error("Failed to update partner"); }
  };

  const handleLogoReplace = async (file) => {
    if (!selected || !file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.put(`${API}/partners/${selected.partner_id}/logo`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Logo updated!");
      fetchPartners();
    } catch { toast.error("Failed to update logo"); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await axios.delete(`${API}/partners/${selected.partner_id}`, { withCredentials: true });
      toast.success("Partner removed");
      setDeleteDialogOpen(false);
      setSelected(null);
      fetchPartners();
    } catch { toast.error("Failed to delete partner"); }
  };

  const toggleActive = async (partner) => {
    try {
      await axios.put(`${API}/partners/${partner.partner_id}`, {
        is_active: !partner.is_active
      }, { withCredentials: true });
      fetchPartners();
    } catch { toast.error("Failed to update"); }
  };

  return (
    <AdminLayout currentPage="Partners">
      <div className="space-y-6" data-testid="admin-partners-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-deep-blue">Partners & Sponsors</h1>
            <p className="text-slate-500 mt-1">Manage your organization's partner logos displayed on the homepage</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-deep-blue text-white hover:bg-deep-blue/90" data-testid="add-partner-btn">
            <Handshake className="w-4 h-4 mr-2" /> Add Partner
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-deep-blue" />
          </div>
        ) : partners.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
            <Handshake className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600">No partners yet</h3>
            <p className="text-slate-400 mt-1">Add your first partner or sponsor</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {partners.map((p) => (
              <div key={p.partner_id} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:shadow-sm transition-shadow" data-testid={`partner-row-${p.partner_id}`}>
                <GripVertical className="w-5 h-5 text-slate-300 shrink-0" />
                <div className="w-16 h-16 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                  {p.logo_url ? (
                    <img src={`${API.replace('/api', '')}${p.logo_url}`} alt={p.name} className="w-full h-full object-contain p-1" />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-deep-blue truncate">{p.name}</h3>
                  {p.website && (
                    <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-sm text-awareness-blue hover:underline flex items-center gap-1 truncate">
                      <Globe className="w-3 h-3" /> {p.website}
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{p.is_active ? "Active" : "Hidden"}</span>
                    <Switch checked={p.is_active} onCheckedChange={() => toggleActive(p)} data-testid={`partner-toggle-${p.partner_id}`} />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => { setSelected({...p}); setEditDialogOpen(true); }} data-testid={`partner-edit-${p.partner_id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => { setSelected(p); setDeleteDialogOpen(true); }} data-testid={`partner-delete-${p.partner_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Partner Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="add-partner-dialog">
            <DialogHeader>
              <DialogTitle>Add New Partner</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Partner Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))} placeholder="e.g. IMPD" data-testid="partner-name-input" />
              </div>
              <div>
                <Label>Website URL</Label>
                <Input value={formData.website} onChange={(e) => setFormData(prev => ({...prev, website: e.target.value}))} placeholder="https://..." data-testid="partner-website-input" />
              </div>
              <div>
                <Label>Logo Image</Label>
                <div className="mt-1">
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => setFormData(prev => ({...prev, file: e.target.files[0]}))} className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-deep-blue/10 file:text-deep-blue hover:file:bg-deep-blue/20" data-testid="partner-file-input" />
                </div>
                {formData.file && <p className="text-xs text-slate-500 mt-1">{formData.file.name}</p>}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={uploading} className="bg-deep-blue text-white" data-testid="partner-submit-btn">
                  {uploading ? "Adding..." : "Add Partner"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Partner Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="edit-partner-dialog">
            <DialogHeader>
              <DialogTitle>Edit Partner</DialogTitle>
            </DialogHeader>
            {selected && (
              <div className="space-y-4">
                <div>
                  <Label>Partner Name</Label>
                  <Input value={selected.name} onChange={(e) => setSelected(prev => ({...prev, name: e.target.value}))} data-testid="edit-partner-name" />
                </div>
                <div>
                  <Label>Website URL</Label>
                  <Input value={selected.website || ""} onChange={(e) => setSelected(prev => ({...prev, website: e.target.value}))} data-testid="edit-partner-website" />
                </div>
                <div>
                  <Label>Replace Logo</Label>
                  <input ref={editFileInputRef} type="file" accept="image/*" onChange={(e) => { if (e.target.files[0]) handleLogoReplace(e.target.files[0]); }} className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-deep-blue/10 file:text-deep-blue hover:file:bg-deep-blue/20" data-testid="edit-partner-file" />
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={selected.is_active} onCheckedChange={(v) => setSelected(prev => ({...prev, is_active: v}))} />
                  <Label>{selected.is_active ? "Active" : "Hidden"}</Label>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleUpdate} className="bg-deep-blue text-white" data-testid="edit-partner-save">Save Changes</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-partner-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Partner</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove "{selected?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-partner">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
